import { clamp } from '../core/math';
import type { Curve, CurveChannels, Point } from '../core/types';

/** Entries in a baked table; one per 8-bit input level. */
export const CURVE_STEPS = 256;

export const LINEAR_CURVE: Curve = [
  { x: 0, y: 0 },
  { x: 1, y: 1 },
];

export function linearCurves(): CurveChannels {
  return { rgb: LINEAR_CURVE, r: LINEAR_CURVE, g: LINEAR_CURVE, b: LINEAR_CURVE };
}

export function isLinearCurve(curve: Curve): boolean {
  return curve.length === 2 && curve[0].x === 0 && curve[0].y === 0 && curve[1].x === 1 && curve[1].y === 1;
}

export function areCurvesLinear(curves: CurveChannels): boolean {
  return (
    isLinearCurve(curves.rgb) &&
    isLinearCurve(curves.r) &&
    isLinearCurve(curves.g) &&
    isLinearCurve(curves.b)
  );
}

/** Sorted by x with duplicates dropped, which is what the spline needs. */
export function normalizeCurve(curve: Curve): Point[] {
  const points = [...curve]
    .map((point) => ({ x: clamp(point.x, 0, 1), y: clamp(point.y, 0, 1) }))
    .sort((a, b) => a.x - b.x);
  const out: Point[] = [];
  for (const point of points) {
    if (out.length > 0 && Math.abs(point.x - out[out.length - 1].x) < 1e-6) out.pop();
    out.push(point);
  }
  return out;
}

/**
 * Fritsch-Carlson monotone cubic interpolation. A plain cubic spline through
 * hand-placed control points overshoots, which shows up as a curve that dips
 * below a point the user dragged up; the monotone variant limits the tangents so
 * the result never wiggles between two points.
 */
function tangents(points: readonly Point[]): number[] {
  const count = points.length;
  const slopes: number[] = [];
  for (let i = 0; i < count - 1; i += 1) {
    const run = points[i + 1].x - points[i].x;
    slopes.push(run > 1e-9 ? (points[i + 1].y - points[i].y) / run : 0);
  }

  const m: number[] = new Array(count).fill(0);
  m[0] = slopes[0];
  m[count - 1] = slopes[count - 2];
  for (let i = 1; i < count - 1; i += 1) {
    if (slopes[i - 1] * slopes[i] <= 0) {
      m[i] = 0;
    } else {
      m[i] = (slopes[i - 1] + slopes[i]) / 2;
    }
  }
  // Clamp so no segment can overshoot its own end points.
  for (let i = 0; i < count - 1; i += 1) {
    if (Math.abs(slopes[i]) < 1e-9) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const alpha = m[i] / slopes[i];
    const beta = m[i + 1] / slopes[i];
    const scale = Math.hypot(alpha, beta);
    if (scale > 3) {
      m[i] = ((3 / scale) * alpha) * slopes[i];
      m[i + 1] = ((3 / scale) * beta) * slopes[i];
    }
  }
  return m;
}

/** Value of the curve at `x`; flat outside the outermost control points. */
export function sampleCurve(curve: Curve, x: number): number {
  const points = normalizeCurve(curve);
  if (points.length === 0) return clamp(x, 0, 1);
  if (points.length === 1) return points[0].y;
  if (x <= points[0].x) return points[0].y;
  if (x >= points[points.length - 1].x) return points[points.length - 1].y;

  const m = tangents(points);
  let i = 0;
  while (i < points.length - 2 && x > points[i + 1].x) i += 1;

  const a = points[i];
  const b = points[i + 1];
  const run = b.x - a.x;
  const t = run > 1e-9 ? (x - a.x) / run : 0;
  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return clamp(h00 * a.y + h10 * run * m[i] + h01 * b.y + h11 * run * m[i + 1], 0, 1);
}

/** The curve baked to one value per input level, ready to become a texture row. */
export function curveTable(curve: Curve, steps = CURVE_STEPS): Float32Array {
  const table = new Float32Array(steps);
  if (isLinearCurve(curve)) {
    for (let i = 0; i < steps; i += 1) table[i] = i / (steps - 1);
    return table;
  }
  for (let i = 0; i < steps; i += 1) table[i] = sampleCurve(curve, i / (steps - 1));
  return table;
}

/**
 * The four curves collapsed into one RGBA lookup row: the composite curve runs
 * first, exactly as in Photoshop, and the per-channel curve is applied to its
 * output. Alpha is left as the ramp so the shader can index the row directly.
 */
export function curvesToLut(curves: CurveChannels, steps = CURVE_STEPS): Uint8Array {
  const composite = curveTable(curves.rgb, steps);
  const perChannel = [curves.r, curves.g, curves.b].map((curve) => curveTable(curve, steps));
  const data = new Uint8Array(steps * 4);
  for (let i = 0; i < steps; i += 1) {
    const base = composite[i];
    const index = Math.round(base * (steps - 1));
    for (let channel = 0; channel < 3; channel += 1) {
      data[i * 4 + channel] = Math.round(perChannel[channel][index] * 255);
    }
    data[i * 4 + 3] = Math.round((i / (steps - 1)) * 255);
  }
  return data;
}

// --- Editing helpers used by the curve editor ---

/** Index of the point within `tolerance` of `at`, or -1. */
export function findCurvePoint(curve: Curve, at: Point, tolerance: number): number {
  let best = -1;
  let bestDistance = tolerance;
  curve.forEach((point, index) => {
    const away = Math.hypot(point.x - at.x, point.y - at.y);
    if (away <= bestDistance) {
      best = index;
      bestDistance = away;
    }
  });
  return best;
}

export function addCurvePoint(curve: Curve, at: Point): { curve: Point[]; index: number } {
  const points = normalizeCurve([...curve, { x: at.x, y: at.y }]);
  return { curve: points, index: points.findIndex((point) => Math.abs(point.x - at.x) < 1e-6) };
}

/**
 * Moves one point. The two end points keep their x, so the curve always spans
 * the whole range, and an interior point cannot cross its neighbours.
 */
export function moveCurvePoint(curve: Curve, index: number, to: Point): Point[] {
  const points = normalizeCurve(curve);
  if (index < 0 || index >= points.length) return points;
  const last = points.length - 1;
  const gap = 1 / (CURVE_STEPS - 1);
  const lowest = index === 0 ? 0 : points[index - 1].x + gap;
  const highest = index === last ? 1 : points[index + 1].x - gap;
  const x = index === 0 ? 0 : index === last ? 1 : clamp(to.x, lowest, Math.max(lowest, highest));
  points[index] = { x, y: clamp(to.y, 0, 1) };
  return points;
}

/** Removes an interior point; the two ends cannot be removed. */
export function removeCurvePoint(curve: Curve, index: number): Point[] {
  const points = normalizeCurve(curve);
  if (index <= 0 || index >= points.length - 1) return points;
  points.splice(index, 1);
  return points;
}
